import React, { useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useReactFlow,
} from "reactflow";

import TriggerNode from "./nodes/TriggerNode";
import GenerateEmailNode from "./nodes/GenerateEmailNode";
import ReviewNode from "./nodes/ReviewNode";
import SendEmailNode from "./nodes/SendEmailNode";
import GeminiModelNode from "./nodes/GeminiModelNode";

const nodeTypes = {
  trigger: TriggerNode,
  generate: GenerateEmailNode,
  review: ReviewNode,
  send: SendEmailNode,
  gemini: GeminiModelNode,
};

const defaultPositions = {
  trigger: { x: 250, y: 50 },
  generate: { x: 250, y: 180 },
  gemini: { x: 500, y: 180 },
  review: { x: 250, y: 320 },
  send: { x: 250, y: 460 },
};

const getEdgeClass = (edgeId, activeEdgeId, completedEdges) => {
  if (edgeId === activeEdgeId) return "flow-edge-active";
  if (completedEdges?.includes(edgeId)) return "flow-edge-done";
  return "flow-edge-idle";
};

export default function FlowCanvas({
  activeNodeId,
  nodeStatus = {},
  onSelectNode,
  onGenerate,
  onTrigger,
  activeEdgeId,
  completedEdges = [],
  onSend,
}) {
  const { setCenter, getZoom } = useReactFlow();

  // 🔥 Critical Fix: Recreate nodes with correct `selected` prop when activeNodeId changes
  const nodes = useMemo(() => [
    {
      id: "trigger",
      type: "trigger",
      position: defaultPositions.trigger,
      data: { status: nodeStatus.trigger || "idle" },
      selected: activeNodeId === "trigger",
    },
    {
      id: "generate",
      type: "generate",
      position: defaultPositions.generate,
      data: {
        status: nodeStatus.generate || "idle",
        onGenerate,
        logic: {},
      },
      selected: activeNodeId === "generate",
    },
    {
      id: "gemini",
      type: "gemini",
      position: defaultPositions.gemini,
      data: { status: nodeStatus.gemini || "idle" },
      selected: activeNodeId === "gemini",
    },
    {
      id: "review",
      type: "review",
      position: defaultPositions.review,
      data: { status: nodeStatus.review || "idle" },
      selected: activeNodeId === "review",
    },
    {
      id: "send",
      type: "send",
      position: defaultPositions.send,
      data: {
        status: nodeStatus.send || "idle",
        onSend,
        sending: false,
      },
      selected: activeNodeId === "send",
    },
  ], [activeNodeId, nodeStatus, onGenerate, onSend]);

  const edges = [
    {
      id: "e-trigger-generate",
      source: "trigger",
      target: "generate",
      animated: activeEdgeId === "e-trigger-generate",
      className: getEdgeClass("e-trigger-generate", activeEdgeId, completedEdges),
      style: activeEdgeId === "e-trigger-generate" ? {
        stroke: 'rgba(0, 212, 255, 0.8)',
        strokeWidth: 3,
        filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))'
      } : completedEdges?.includes("e-trigger-generate") ? {
        stroke: 'rgba(0, 255, 136, 0.8)',
        strokeWidth: 2,
        filter: 'drop-shadow(0 0 6px rgba(0, 255, 136, 0.4))'
      } : {
        stroke: 'rgba(0, 212, 255, 0.3)',
        strokeWidth: 2
      }
    },
    {
      id: "e-generate-gemini",
      source: "generate",
      sourceHandle: "gemini",
      target: "gemini",
      animated: activeEdgeId === "e-generate-gemini",
      className: getEdgeClass("e-generate-gemini", activeEdgeId, completedEdges),
      style: activeEdgeId === "e-generate-gemini" ? {
        stroke: 'rgba(0, 212, 255, 0.8)',
        strokeWidth: 3,
        filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))'
      } : completedEdges?.includes("e-generate-gemini") ? {
        stroke: 'rgba(0, 255, 136, 0.8)',
        strokeWidth: 2,
        filter: 'drop-shadow(0 0 6px rgba(0, 255, 136, 0.4))'
      } : {
        stroke: 'rgba(0, 212, 255, 0.3)',
        strokeWidth: 2
      }
    },
    {
      id: "e-generate-review",
      source: "generate",
      sourceHandle: "review",
      target: "review",
      animated: activeEdgeId === "e-generate-review",
      className: getEdgeClass("e-generate-review", activeEdgeId, completedEdges),
      style: activeEdgeId === "e-generate-review" ? {
        stroke: 'rgba(0, 212, 255, 0.8)',
        strokeWidth: 3,
        filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))'
      } : completedEdges?.includes("e-generate-review") ? {
        stroke: 'rgba(0, 255, 136, 0.8)',
        strokeWidth: 2,
        filter: 'drop-shadow(0 0 6px rgba(0, 255, 136, 0.4))'
      } : {
        stroke: 'rgba(0, 212, 255, 0.3)',
        strokeWidth: 2
      }
    },
    {
      id: "e-review-send",
      source: "review",
      target: "send",
      animated: activeEdgeId === "e-review-send",
      className: getEdgeClass("e-review-send", activeEdgeId, completedEdges),
      style: activeEdgeId === "e-review-send" ? {
        stroke: 'rgba(0, 212, 255, 0.8)',
        strokeWidth: 3,
        filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))'
      } : completedEdges?.includes("e-review-send") ? {
        stroke: 'rgba(0, 255, 136, 0.8)',
        strokeWidth: 2,
        filter: 'drop-shadow(0 0 6px rgba(0, 255, 136, 0.4))'
      } : {
        stroke: 'rgba(0, 212, 255, 0.3)',
        strokeWidth: 2
      }
    },
  ];

  const focusNode = (nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (node) {
      const x = node.position.x + 150;
      const y = node.position.y + 80;
      setCenter(x, y, { zoom: getZoom(), duration: 800 });
    }
  };

  // Inline styles for ReactFlow container
  const reactFlowStyles = {
    width: "100%",
    height: "100%",
    background: 'transparent',
    fontFamily: "'Raleway','Poppins', sans-serif",
  };

  // Background pattern
  const backgroundStyle = {
    backgroundImage: 
      'linear-gradient(rgba(0, 212, 255, 0.05) 1px, transparent 1px), ' +
      'linear-gradient(90deg, rgba(0, 212, 255, 0.05) 1px, transparent 1px)',
    backgroundSize: '40px 40px',
    opacity: 0.3,
  };

  // Controls button styles
  const controlsButtonStyle = (isFirst = false) => ({
    background: 'transparent',
    border: '1px solid rgba(0, 212, 255, 0.3)',
    borderLeft: isFirst ? '1px solid rgba(0, 212, 255, 0.3)' : 'none',
    color: 'rgba(255, 255, 255, 0.7)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const controlsButtonHoverStyle = {
    background: 'rgba(0, 212, 255, 0.1)',
    color: '#ffffff',
    borderColor: 'rgba(0, 212, 255, 0.5)',
  };

  return (
    <div style={{
      width: "100%",
      height: "600px",
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '16px',
      border: '1px solid rgba(0, 212, 255, 0.2)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Tech background overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 
          'radial-gradient(circle at 20% 80%, rgba(0, 212, 255, 0.05), transparent 50%),' +
          'radial-gradient(circle at 80% 20%, rgba(147, 51, 234, 0.05), transparent 50%)',
        pointerEvents: 'none',
        zIndex: 1,
      }} />
      
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={(_, node) => {
          if (node.id === "trigger") {
            onTrigger?.();
            onSelectNode?.("generate");
            focusNode("generate");
            return;
          }
          onSelectNode?.(node.id);
        }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        style={reactFlowStyles}
        // Customize default node styles
        defaultEdgeOptions={{
          type: 'smoothstep',
          style: {
            stroke: 'rgba(0, 212, 255, 0.3)',
            strokeWidth: 2,
            strokeLinecap: 'round',
          },
        }}
      >
        <Background 
          style={backgroundStyle}
          gap={40}
          size={1}
          color="rgba(0, 212, 255, 0.05)"
        />
        
        <MiniMap 
          style={{
            background: 'rgba(13, 27, 42, 0.9)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)',
          }}
          nodeStrokeColor={() => 'rgba(0, 212, 255, 0.5)'}
          nodeColor={() => 'rgba(0, 212, 255, 0.3)'}
          maskColor="rgba(0, 212, 255, 0.1)"
        />
        
        <Controls
          style={{
            background: 'rgba(13, 27, 42, 0.9)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
          }}
          showInteractive={false}
        >
          {/* Custom controls with hover effects */}
          {(ref) => (
            <div ref={ref}>
              <button
                onClick={() => ref.current?.zoomIn()}
                style={controlsButtonStyle(true)}
                onMouseEnter={(e) => Object.assign(e.target.style, controlsButtonHoverStyle)}
                onMouseLeave={(e) => Object.assign(e.target.style, controlsButtonStyle(true))}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
              </button>
              <button
                onClick={() => ref.current?.zoomOut()}
                style={controlsButtonStyle()}
                onMouseEnter={(e) => Object.assign(e.target.style, controlsButtonHoverStyle)}
                onMouseLeave={(e) => Object.assign(e.target.style, controlsButtonStyle())}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13H5v-2h14v2z"/>
                </svg>
              </button>
              <button
                onClick={() => ref.current?.fitView()}
                style={controlsButtonStyle()}
                onMouseEnter={(e) => Object.assign(e.target.style, controlsButtonHoverStyle)}
                onMouseLeave={(e) => Object.assign(e.target.style, controlsButtonStyle())}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 8v8l-4-4 4-4zm6 4l-4 4V8l4 4z"/>
                </svg>
              </button>
            </div>
          )}
        </Controls>
        
        {/* Custom edge animations */}
        <svg style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
          <defs>
            {/* Active edge gradient */}
            <linearGradient id="edge-active-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(0, 212, 255, 0)" />
              <stop offset="50%" stopColor="rgba(0, 212, 255, 0.8)" />
              <stop offset="100%" stopColor="rgba(0, 212, 255, 0)" />
            </linearGradient>
            {/* Success edge gradient */}
            <linearGradient id="edge-done-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(0, 255, 136, 0)" />
              <stop offset="50%" stopColor="rgba(0, 255, 136, 0.8)" />
              <stop offset="100%" stopColor="rgba(0, 255, 136, 0)" />
            </linearGradient>
          </defs>
        </svg>
      </ReactFlow>

      {/* Mobile responsive adjustments */}
      <style>{`
        @media (max-width: 768px) {
          .react-flow__minimap {
            display: none !important;
          }
          .react-flow__controls {
            transform: scale(0.8);
            transform-origin: bottom right;
          }
        }
        
        /* Handle animations */
        .react-flow__handle {
          width: 10px;
          height: 10px;
          background: linear-gradient(135deg, #00d4ff, #9333ea);
          border: 2px solid rgba(13, 27, 42, 0.9);
          transition: all 0.3s ease;
        }
        
        .react-flow__handle:hover {
          background: linear-gradient(135deg, #00ff88, #00d4ff);
          transform: scale(1.2);
          box-shadow: 0 0 10px rgba(0, 212, 255, 0.6);
        }
        
        /* Edge animations */
        .react-flow__edge.animated .react-flow__edge-path {
          animation: edgeFlow 2s linear infinite;
          stroke-dasharray: 10;
        }
        
        @keyframes edgeFlow {
          0% { stroke-dashoffset: 20; }
          100% { stroke-dashoffset: 0; }
        }
        
        /* Active edge pulse */
        .flow-edge-active {
          animation: edgePulse 1.5s ease-in-out infinite !important;
        }
        
        @keyframes edgePulse {
          0%, 100% { 
            stroke-opacity: 0.8;
            stroke-width: 2;
          }
          50% { 
            stroke-opacity: 1;
            stroke-width: 3;
          }
        }
        
        /* Node selection glow */
        .react-flow__node.selected {
          border-color: rgba(0, 212, 255, 0.6) !important;
          box-shadow: 
            0 8px 30px rgba(0, 0, 0, 0.5),
            0 0 20px rgba(0, 212, 255, 0.3) !important;
          background: rgba(0, 212, 255, 0.05) !important;
        }
      `}</style>
    </div>
  );
}